import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputText} from "primereact/inputtext";
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";

import {DOC_PROPERTY_SWOT_NEGATIVES, DOC_PROPERTY_SWOT_POSITIVES} from "@reva/shared";

import {CellInputActionIcon} from "../../component/paramInput/InputGrossFloorArea";
import {useParameterValue} from "../../component/paramInput/paramHooks";
import {ROW_ACTION_COLUMN_STYLE} from "../../component/paramInput/RowDeleteIcon";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {selectSwotHasStandardSentence, selectSwotModel} from "../../redux/swotSelectors";

export function ObjectInputSwotAnalyse({header, parameter}) {
    const readOnly = useSelector(selectReadOnly);
    const {onValueChange: setModelValue} = useParameterValue(parameter, "DOC_PROPERTY");
    const [lastUpdate, setLastUpdate] = useState(Date.now);

    // Nicht direkt die Document Property, sondern das angezeigte Modell: solange die Liste noch nicht
    // gespeichert ist, enthält es zusätzlich die automatischen Standardsätze (#18102). Bearbeitet der
    // Nutzer eine Zeile, wird dieses Modell (inkl. Standardsätze) zur gespeicherten Liste.
    const effectiveModel = useSelector(selectSwotModel(parameter));

    // Die erste Nutzer-Eingabe beendet die Automatik für BEIDE Listen. Trägt die jeweils andere Liste
    // gerade einen automatischen Standardsatz, wird sie deshalb mitgespeichert — sonst verschwände er
    // im selben Moment wieder (#18102).
    const otherParameter =
        parameter === DOC_PROPERTY_SWOT_POSITIVES ? DOC_PROPERTY_SWOT_NEGATIVES : DOC_PROPERTY_SWOT_POSITIVES;
    const otherModel = useSelector(selectSwotModel(otherParameter));
    const otherHasStandardSentence = useSelector(selectSwotHasStandardSentence(otherParameter));
    const {onValueChange: setOtherModelValue} = useParameterValue(otherParameter, "DOC_PROPERTY");

    const persistModel = (model) => {
        if (otherHasStandardSentence) setOtherModelValue(otherModel, null, true);
        setModelValue(model, null, true);
    };

    function convertModelToRows(model, lastUpdate) {
        let index = 0;
        if (!model?.items || model.items.length === 0)
            return readOnly ? [] : [{newRow: true, index: 0, description: undefined, updated: lastUpdate}];
        const convertedRows = model.items.map((item) => {
            return {
                ...item,
                newRow: false,
                description: item.description || "",
                index: index++,
                updated: lastUpdate,
            };
        });
        if (readOnly) return convertedRows;
        const newRow = {
            newRow: true,
            index,
            description: "",
            updated: lastUpdate,
        };
        return [...convertedRows, newRow];
    }

    function SwotCell({rowData, onChange}) {
        const value = rowData.description || "";

        const [localValue, setLocalValue] = useState(value);

        useEffect(() => {
            setLocalValue(value);
        }, [value]);

        return (
            <InputText
                style={{width: "100%"}}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={(e) => {
                    onChange(e.target.value);
                }}
            />
        );
    }

    /**
     * Change a value in a row.
     * @param {number} rowIndex index of the row
     * @param {string} field field to change
     * @param value new value
     */
    const onRowChange = (rowIndex, field, value) => {
        const newModel = structuredClone(effectiveModel) || {items: []};
        const items = newModel.items;
        if (rowIndex === undefined || rowIndex === null) {
            console.warn("No rowIndex given for", field, value);
            return;
        }
        if (rowIndex < 0 || rowIndex > items.length) {
            console.warn("Invalid row Index", rowIndex);
            return;
        }
        if (rowIndex === items.length) {
            if (!value) {
                return;
            }
            // Add new row
            items[rowIndex] = {description: ""};
        }
        if (field !== "X") items[rowIndex][field] = value;

        // Check if line should be removed
        const shouldBeRemoved = field === "X" || !items[rowIndex].description;
        if (rowIndex === 0 && items.length === 1 && shouldBeRemoved) {
            // Bewusst die leere Liste speichern statt null: eine gespeicherte (auch leere) Liste gilt als
            // vom Nutzer gepflegt, so kommen weder die BesiConverter-Vorbefüllung noch die automatischen
            // Standardsätze (#18102) nach dem Löschen der letzten Zeile zurück.
            persistModel({items: []});
        } else if (shouldBeRemoved) {
            persistModel({items: items.filter((v, i) => rowIndex !== i)});
        } else {
            persistModel({items});
        }
        setLastUpdate(Date.now());
    };

    const rows = convertModelToRows(effectiveModel, lastUpdate);

    return (
        <DataTable
            className="p-inputtext-sm text-sm inputtext"
            value={rows}
            dataKey="index"
            pt={{headerRow: {className: "font-normal"}}}
            size="small"
        >
            <Column
                style={ROW_ACTION_COLUMN_STYLE}
                body={(rowData, {rowIndex}) => (
                    <CellInputActionIcon rowData={rowData} onDelete={() => onRowChange(rowIndex, "X", null)} />
                )}
            />
            <Column
                header={header || "Beschreibung"}
                field="description"
                body={(rowData, {rowIndex, field}) => (
                    <SwotCell rowData={rowData} onChange={(value) => onRowChange(rowIndex, field, value)} />
                )}
            />
        </DataTable>
    );
}
